import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { submitEnquiry, ApiError } from "../services/api";
import usePageTracking from "../hooks/usePageTracking";
import { trackEvent } from '../services/mixpanel';

const EnquiryPopup = ({ isOpen, onClose }) => {
  const pageInfo = usePageTracking();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    interest: "Buying",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const isFormValid =
    formData.name.trim().length > 0 && formData.phone.trim().length > 0;

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (formData.phone.length < 10) {
      newErrors.phone = "Please enter a valid phone number";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      // Track validation errors
      trackEvent('Enquiry Form Validation Failed', {
        fields_with_errors: Object.keys(newErrors),
        error_count: Object.keys(newErrors).length,
        form_type: 'Enquiry Popup Form'
      });

      return;
    }

    setIsSubmitting(true);

    const MAX_RETRIES = 2;
    let attempt = 0;
    let lastError = null;

    while (attempt <= MAX_RETRIES) {
      try {
        const submissionData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          interest: formData.interest,
          page_url: pageInfo.url || (typeof window !== "undefined" ? window.location.href : "")
        };

        await submitEnquiry(submissionData);

        // Track ONLY successful submission with actual field values
        trackEvent('Enquiry Form Submitted', {
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          interest: formData.interest,
          attempt: attempt + 1
        });

        // Reset form on successful submission
        setFormData({
          name: "",
          phone: "",
          email: "",
          interest: "Buying",
        });

        setShowSuccessMessage(true);
        setIsSubmitting(false);
        return;
      } catch (error) {
        lastError = error;
        attempt++;

        console.error(`Submission attempt ${attempt} failed:`, error);

        if (error.status === 408 && attempt <= MAX_RETRIES) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          console.log(`Retrying in ${delay}ms... (Attempt ${attempt + 1} of ${MAX_RETRIES + 1})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        break;
      }
    }

    console.error("Submission error after", attempt, "attempts:", lastError);

    // Track submission failures
    trackEvent('Enquiry Form Submission Failed', {
      name: formData.name,
      error_message: lastError?.message || 'Unknown error',
      error_status: lastError?.status,
      attempts: attempt
    });

    let errorMessage = "Something went wrong. Please try again.";

    if (lastError instanceof ApiError) {
      errorMessage = lastError.message;

      if (lastError.status === 408) {
        errorMessage = "The request is taking longer than expected. Your enquiry may have been submitted successfully. Please check your email or contact us directly if you don't receive a confirmation.";
      }
    }

    alert(errorMessage);
    setIsSubmitting(false);
  };

  const handleClose = useCallback(() => {
    trackEvent('Enquiry Form Closed', {
      form_completed: showSuccessMessage
    });
    onClose();
  }, [showSuccessMessage, onClose]);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (!isOpen) {
      setShowSuccessMessage(false);
    } else {
      trackEvent('Enquiry Form Opened', {
        timestamp: new Date().toISOString()
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const popupContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
      <div
        className="bg-white shadow-2xl max-w-lg w-full max-h-[90vh] rounded-md overflow-y-auto relative animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-500 hover:text-gray-700 z-10 p-2 bg-white rounded-full shadow-md"
          aria-label="Close popup"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {!showSuccessMessage ? (
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Enquire Now</h2>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {/* Name Field */}
              <div>
                <label
                  htmlFor="popup-name"
                  className="block text-sm font-bold text-gray-700 mb-1"
                >
                  Your Name{" "}
                  <span className="text-red-500" aria-label="required">
                    *
                  </span>
                </label>
                <input
                  type="text"
                  id="popup-name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your name"
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD200] transition-colors ${
                    errors.name ? "border-red-500 bg-red-50" : "border-gray-300"
                  }`}
                  autoComplete="off"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              {/* Phone Field */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  Phone number{" "}
                  <span className="text-red-500" aria-label="required">
                    *
                  </span>
                </label>
                <PhoneInput
                  country={"ae"}
                  value={formData.phone}
                  onChange={(phone) =>
                    setFormData((prev) => ({ ...prev, phone }))
                  }
                  onFocus={() => {
                    if (errors.phone) {
                      setErrors((prev) => ({ ...prev, phone: "" }));
                    }
                  }}
                  inputStyle={{
                    width: "100%",
                    height: "48px",
                    fontSize: "16px",
                    borderColor: errors.phone ? "#ef4444" : "#d1d5db",
                    backgroundColor: errors.phone ? "#fef2f2" : "#ffffff",
                  }}
                  buttonStyle={{
                    borderColor: errors.phone ? "#ef4444" : "#d1d5db",
                    backgroundColor: "#f9fafb",
                  }}
                  dropdownStyle={{
                    maxHeight: "200px",
                  }}
                  searchStyle={{
                    padding: "8px",
                    fontSize: "14px",
                  }}
                  enableSearch={true}
                  searchPlaceholder="Search countries"
                  placeholder="Enter phone number"
                  specialLabel=""
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600" role="alert">
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label
                  htmlFor="popup-email"
                  className="block text-sm font-bold text-gray-700 mb-1"
                >
                  Customer Email ID{" "}
                  <span className="text-red-500" aria-label="required">
                    *
                  </span>
                </label>
                <input
                  type="email"
                  id="popup-email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  autoComplete="off"
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD200] transition-colors ${
                    errors.email ? "border-red-500 bg-red-50" : "border-gray-300"
                  }`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Interest Selection */}
              <fieldset className="bg-[#F5F1E6] rounded-lg p-4 w-[80%] mx-auto overflow-hidden">
                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6">
                  <p className="text-sm font-semibold text-[#9E3F2E] shrink-0">
                    I am interested in:
                  </p>

                  <div className="flex items-center gap-4">
                    <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="radio"
                        name="interest"
                        value="Buying"
                        checked={formData.interest === "Buying"}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-[#9E3F2E] focus:ring-[#9E3F2E] border-gray-300"
                      />
                      <span>Buying</span>
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="radio"
                        name="interest"
                        value="Renting"
                        checked={formData.interest === "Renting"}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-[#9E3F2E] focus:ring-[#9E3F2E] border-gray-300"
                      />
                      <span>Renting</span>
                    </label>
                  </div>
                </div>
              </fieldset>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || !isFormValid}
                className={`w-1/2 mx-auto flex justify-center items-center font-semibold py-3 px-6 rounded-md transition-colors duration-200 mt-6 ${
                  isFormValid && !isSubmitting
                    ? "bg-gray-600 hover:bg-gray-700 text-white"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </button>

              {isSubmitting && (
                <p className="text-center text-sm text-blue-600 mt-2 animate-pulse">
                  Please wait, your enquiry is being processed...
                </p>
              )}

              {!isSubmitting && (
                <p className="text-center italic text-sm text-gray-600 mt-4">
                  Quick response always , we'll call you{" "}
                  <span className="font-semibold">within 1 hour</span>
                </p>
              )}
            </form>
          </div>
        ) : (
          <div className="submit-message flex flex-col justify-center items-center text-center p-6 min-h-[400px]">
            <div className="tick-animation mb-4">
              <img
                src="/images/tick_animation.gif"
                alt="Success"
                className="w-24 h-24 md:w-32 md:h-32"
              />
            </div>
            <div className="flex flex-col justify-center items-center text-center space-y-3 max-w-lg mx-auto px-4">
              <p className="text-lg md:text-xl font-semibold leading-relaxed">
                Query submitted
              </p>
              <p className="text-xl md:text-2xl font-semibold leading-snug">
                Wallah! You're taking a worthy step towards your Business!
              </p>
              <h6 className="text-sm md:text-base leading-normal font-normal">
                Our expert will get to you <strong>soon!</strong>
              </h6>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
};

export default EnquiryPopup;